import { useRef } from 'react'

/**
 * A sass line drawn once per transition into a state, not once per render
 * while the state holds. A live query ticking, a keystroke that leaves a
 * search still empty, a pending count changing on an unbroken outage — none
 * of those are the reader hearing the same moment twice, so none of them may
 * cost a fresh draw against the no-repeat pool.
 *
 * `key` names the state to draw for; `null` means "not in that state, say
 * nothing." Passing the same key across renders skips `draw` entirely — this
 * is a ref comparison done in the render body, not a `useMemo`, precisely so
 * a caller can read whatever reactive values it wants inside `draw` without
 * biome asking for them all as effect dependencies: `draw` is a value
 * *derived from* `key`, and `key` is the true, exhaustive dependency.
 */
export function useSassOnce(key: string | null, draw: () => string): string | null {
  const cache = useRef<{ key: string | null; line: string | null }>({
    key: Symbol('unset') as unknown as string,
    line: null,
  })
  if (cache.current.key !== key) {
    cache.current = { key, line: key == null ? null : draw() }
  }
  return cache.current.line
}
